import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router';
import type { Session } from '../../api/session';
import { getObject, getObjectAddresses, getPassesPage, type ObjectInfo } from '../../api/tenant';
import { passOptions } from '../../domain/availability';
import { GuestType, PassStatus } from '../../domain/passes';
import { Skeleton } from '../../ui/Skeleton';
import { LoadFailed } from '../common/LoadFailed';
import { PassRow } from '../common/PassRow';
import p from '../passes/passes.module.css';
import f from '../pass-form/passForm.module.css';

const PERMANENT = 3;

/**
 * «Мои машины» (roadmap P2-14): the family's permanent car passes of this посёлок in one place. A car opens its
 * card, where «Изменить» replaces the number and «Закрыть» removes it; a new car is the usual form, permanent.
 */
export function MyCarsScreen({ session, object }: { session: Session; object: ObjectInfo }) {
  const addresses = useQuery({ queryKey: ['addresses', object.id], queryFn: () => getObjectAddresses(session, object.id) });
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const cars = useQuery({
    queryKey: ['passes', 'my-cars'],
    queryFn: () => getPassesPage(session, { durationType: PERMANENT, guestType: GuestType.Vehicle, statuses: [PassStatus.Expected, PassStatus.Inside] }),
  });

  const permanent = useMemo(() => passOptions(details.data?.settings ?? {}).permanent, [details.data]);
  const ids = new Set((addresses.data ?? []).map((a) => a.id));
  // The filter is checked again here: the list must not show a one-day guest as "my car".
  const list = (cars.data?.items ?? []).filter(
    (c) => c.durationType === PERMANENT && c.guestType === GuestType.Vehicle && ids.has(c.address.id),
  );

  const error = cars.error ?? addresses.error ?? details.error;
  return (
    <main className={p.page}>
      <h1 className={p.title}>Мои машины</h1>
      <p className={f.where}>Постоянные пропуска машин семьи в {object.name}</p>
      {error ? (
        <LoadFailed error={error} onRetry={() => { void cars.refetch(); void addresses.refetch(); void details.refetch(); }} />
      ) : !cars.data || !details.data || !addresses.data ? (
        <Skeleton height={120} />
      ) : (
        <>
          {list.length === 0 ? (
            <p className={p.empty}>Постоянных пропусков на машины пока нет</p>
          ) : (
            <ul className={p.list} aria-label="Машины">
              {list.map((c) => (
                <li key={c.id}>
                  <PassRow pass={c} />
                </li>
              ))}
            </ul>
          )}
          {permanent ? (
            <>
              <Link to="/new?duration=permanent" className={f.submit}>
                + Добавить машину
              </Link>
              <p className={f.hint}>
                {permanent === 'request' ? 'Новую машину согласует управляющая компания. ' : ''}Сменили номер или продали машину — откройте её и
                нажмите «Изменить» или «Закрыть пропуск».
              </p>
            </>
          ) : (
            <>
              <p className={f.hint}>На этом объекте постоянные пропуска оформляет управляющая компания.</p>
              <Link to="/requests/new" className={f.secondary}>
                Написать в УК
              </Link>
            </>
          )}
        </>
      )}
    </main>
  );
}
